import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { displayToast } from '../../_helpers/commonFunctions';
import { getDeficiencyDetails } from '../../api/updateDeficiencyDetails';
import Modal from '../Modal/Modal.jsx';
import Button from '../Button';
import DeficiencyModalBody from './DeficiencyModalBody.jsx';
import './DeficiencyModal.css';

/**
 * DeficiencyModal Component
 *
 * Displays deficiency details in a modal dialog with support for:
 * - Basic deficiency information (from populated API response)
 * - "View Full Details" functionality (fetches complete deficiency data)
 * - Loading states and error handling
 * - Keyboard accessibility (ESC to close)
 * - Focus management (returns focus to trigger element)
 */
class DeficiencyModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      fullDeficiency: null,
      isFetchingDetails: false,
      error: null,
      showFullDetails: false,
      canRetry: false,
    };
    this.modalRef = React.createRef();
    this.triggerElement = null;
  }

  componentDidMount() {
    // Store the element that triggered the modal for focus return
    this.triggerElement = document.activeElement;

    // Add ESC key listener
    document.addEventListener('keydown', this.handleKeyDown);

    // Add click outside listener
    document.addEventListener('mousedown', this.handleClickOutside);

    // Automatically fetch full deficiency details when modal opens
    this.fetchFullDeficiencyDetails();
  }

  componentWillUnmount() {
    // Remove event listeners
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousedown', this.handleClickOutside);

    // Return focus to trigger element
    if (this.triggerElement && this.triggerElement.focus) {
      this.triggerElement.focus();
    }
  }

  /**
   * Handle ESC key press to close modal
   */
  handleKeyDown = (event) => {
    if (event.key === 'Escape' || event.keyCode === 27) {
      this.handleClose();
    }
  };

  /**
   * Handle click outside modal to close
   */
  handleClickOutside = (event) => {
    const overlayModal = document.getElementById('overlayModal');

    // Close if clicking on the overlay but not on the modal content
    if (overlayModal && event.target === overlayModal) {
      this.handleClose();
    }
  };

  /**
   * Get error message and retry status based on error type
   */
  getErrorInfo = (error) => {
    // Handle specific error cases
    if (error.status === 404) {
      return {
        message: 'This deficiency has been deleted or does not exist.',
        canRetry: false,
      };
    }
    
    if (error.status === 401) {
      return {
        message: 'Your session has expired. Please log in again.',
        canRetry: false,
      };
    }
    
    if (error.status === 403) {
      return {
        message: 'You do not have permission to view this deficiency.',
        canRetry: false,
      };
    }
    
    if (error.status === 500 || error.status === 502 || error.status === 503) {
      return {
        message: 'Server error. Please try again in a moment.',
        canRetry: true,
      };
    }
    
    if (!navigator.onLine) {
      return {
        message: 'Network error. Please check your connection and try again.',
        canRetry: true,
      };
    }
    
    if (error.message && error.message.includes('Invalid response format')) {
      return {
        message: 'Received invalid data from server. Please contact support.',
        canRetry: false,
      };
    }
    
    if (error.message && error.message.includes('Malformed deficiency data')) {
      return {
        message: 'Deficiency data is incomplete. Please contact support.',
        canRetry: false,
      };
    }
    
    // Generic network or unknown error
    return {
      message: 'Unable to load deficiency details. Please try again.',
      canRetry: true,
    };
  };

  /**
   * Validate deficiency data structure
   */
  validateDeficiency = (deficiency) => {
    if (!deficiency) {
      return { valid: false, error: 'No deficiency data available.' };
    }

    if (!deficiency._id) {
      console.error('[DeficiencyModal] Deficiency missing _id field:', deficiency);
      return { valid: false, error: 'Invalid deficiency data: missing ID.' };
    }

    if (typeof deficiency._id !== 'string' || deficiency._id.trim() === '') {
      console.error('[DeficiencyModal] Invalid deficiency ID format:', deficiency._id);
      return { valid: false, error: 'Invalid deficiency ID format.' };
    }

    return { valid: true };
  };

  /**
   * Validate API response structure
   */
  validateResponse = (response) => {
    if (!response) {
      console.error('[DeficiencyModal] Empty response from API');
      throw new Error('Empty response from server');
    }

    if (!response.body) {
      console.error('[DeficiencyModal] Response missing body:', response);
      throw new Error('Invalid response format: missing body');
    }

    if (!response.body.data) {
      console.error('[DeficiencyModal] Response body missing data:', response.body);
      throw new Error('Invalid response format: missing data');
    }

    const deficiencyData = response.body.data;
    if (!deficiencyData._id) {
      console.error('[DeficiencyModal] Deficiency data missing _id:', deficiencyData);
      throw new Error('Malformed deficiency data: missing ID');
    }

    return deficiencyData;
  };

  /**
   * Fetch full deficiency details from API
   */
  fetchFullDeficiencyDetails = async (isRetry = false) => {
    const { deficiency } = this.props;

    // Validate deficiency data
    const validation = this.validateDeficiency(deficiency);
    if (!validation.valid) {
      console.error('[DeficiencyModal] Validation failed:', validation.error);
      this.setState({
        isFetchingDetails: false,
        error: validation.error,
      });
      return;
    }

    this.setState({ isFetchingDetails: true, error: null });

    try {
      const response = await getDeficiencyDetails(deficiency._id);
      const deficiencyData = this.validateResponse(response);

      this.setState({
        fullDeficiency: deficiencyData,
        isFetchingDetails: false,
        showFullDetails: true,
        error: null,
      });
    } catch (error) {
      console.error('[DeficiencyModal] Error fetching deficiency details:', {
        deficiencyId: deficiency._id,
        error: error,
        status: error.status,
        message: error.message,
        isRetry: isRetry,
      });

      const errorInfo = this.getErrorInfo(error);

      this.setState({
        isFetchingDetails: false,
        error: errorInfo.message,
        canRetry: errorInfo.canRetry,
      });

      // Only show toast on first attempt, not on retry
      if (!isRetry) {
        displayToast(errorInfo.message, 'error');
      }
    }
  };

  /**
   * Retry fetching deficiency details
   */
  handleRetry = () => {
    this.fetchFullDeficiencyDetails(true);
  };

  /**
   * Close modal and reset state
   */
  handleClose = () => {
    const { onClose } = this.props;

    // Reset state
    this.setState({
      fullDeficiency: null,
      isFetchingDetails: false,
      error: null,
      showFullDetails: false,
      canRetry: false,
    });

    // Call parent close handler
    if (onClose) {
      onClose();
    }
  };

  /**
   * Render modal footer with action buttons
   */
  renderModalFooter = () => {
    const { canRetry, error } = this.state;

    return (
      <div className="deficiency-modal-footer">
        <div className="deficiency-modal-footer-right">
          {canRetry && error && (
            <Button 
              onClick={this.handleRetry} 
              text="Retry"
              color="#28a745"
              className="mr-2"
            />
          )}
          <Button 
            onClick={this.handleClose} 
            text="Close"
            color="#4DBCE9"
          />
        </div>
      </div>
    );
  };

  render() {
    const { isOpen, deficiency } = this.props;
    const { fullDeficiency, isFetchingDetails, error, showFullDetails } =
      this.state;

    if (!isOpen) {
      return null;
    }

    // Use full deficiency data if available, otherwise use basic deficiency data
    const deficiencyData =
      showFullDetails && fullDeficiency ? fullDeficiency : deficiency;

    return (
      <div ref={this.modalRef}>
        <Modal
          modalHeading="Deficiency Details"
          ModalBody={DeficiencyModalBody}
          ModalBodyProps={{
            deficiency: deficiencyData,
            isFetching: isFetchingDetails,
            error,
          }}
          ModalFooter={this.renderModalFooter}
          handleClickCross={this.handleClose}
          modalId="deficiencyModal"
          modalClass="modal-dialog modal-dialog-centered"
          isCrossVisible
        />
      </div>
    );
  }
}

DeficiencyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  deficiency: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    status: PropTypes.string,
    description: PropTypes.string,
    reportCategory: PropTypes.string,
    createdAt: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

DeficiencyModal.defaultProps = {
  deficiency: null,
};

export default DeficiencyModal;
